import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID, Optional, SkipSelf } from '@angular/core';
import { environment } from '@environments/environment';
import { CartItem } from '@interfaces/cart-item';
import * as _ from 'lodash';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
/* import { PixelService } from 'ngx-pixel'; */
import { BehaviorSubject, Observable, Subject, timer } from 'rxjs';
import { map, tap } from 'rxjs/operators';

interface CartTotal {
    title: string;
    price: number;
    type: 'shipping' | 'fee' | 'tax' | 'other';
}

interface CartData {
    items: any[];
    quantity: number;
    subtotal: number;
    totals: CartTotal[];
    total: number;
}

interface UserCart {
    cartId: string,
    items: any[]
}

@Injectable({
    providedIn: 'root'
})
export class CartService {
    private data: any = {
        items: [],
        quantity: 0,
        subtotal: 0,
        totals: [],
        total: 0
    };


    private itemsSubject$: BehaviorSubject<CartItem[]> = new BehaviorSubject(this.data.items);
    private quantitySubject$: BehaviorSubject<number> = new BehaviorSubject(this.data.quantity);
    private subtotalSubject$: BehaviorSubject<number> = new BehaviorSubject(this.data.subtotal);
    private shippingTotalSubject$: BehaviorSubject<number> = new BehaviorSubject(null);
    private taxTotalSubject$: BehaviorSubject<number> = new BehaviorSubject(null);
    private discountTotalSubject$: BehaviorSubject<number> = new BehaviorSubject(null);
    private totalsSubject$: BehaviorSubject<CartTotal[]> = new BehaviorSubject(this.data.totals);
    private totalSubject$: BehaviorSubject<number> = new BehaviorSubject(this.data.total);
    private onAddingSubject$: Subject<any> = new Subject();
    private cartItemsSubject$: BehaviorSubject<any[]> = new BehaviorSubject([]);
    public orderNoteSubject$: BehaviorSubject<any> = new BehaviorSubject('');

    public userCart : UserCart = {} as UserCart;


    get items(): ReadonlyArray<any> {
        return this.data.items;
    }

    get quantity(): number {
        return this.quantitySubject$.value
    }

    get cartItemsValue() {
        return this.cartItemsSubject$.value
    }

    set cartItemsValue(cart: any) {
        let carts = this.cartItemsValue;

        if (!_.isEmpty(cart)) {
            let cartIndex = _.findIndex(carts, ["_id", cart._id]);

            if (cartIndex > -1) {
                carts[cartIndex] = cart;
            } else
                carts.push(cart);

            this.cartItemsSubject$.next(carts)
            this.calculateCart();
        }
    }

    get discounttTotal() {
        return this.discountTotalSubject$.value
    }

    readonly items$: Observable<CartItem[]> = this.itemsSubject$.asObservable();
    readonly quantity$: Observable<number> = this.quantitySubject$.asObservable();
    readonly subtotal$: Observable<number> = this.subtotalSubject$.asObservable();
    readonly shippingTotal$: Observable<number> = this.shippingTotalSubject$.asObservable();
    readonly taxtTotal$: Observable<number> = this.taxTotalSubject$.asObservable();
    readonly discounttTotal$: Observable<number> = this.discountTotalSubject$.asObservable();
    readonly totals$: Observable<CartTotal[]> = this.totalsSubject$.asObservable();
    readonly total$: Observable<number> = this.totalSubject$.asObservable();
    readonly onAdding$: Observable<any> = this.onAddingSubject$.asObservable();
    readonly cartItems$: Observable<any[]> = this.cartItemsSubject$.asObservable();

    constructor(
        @Inject(PLATFORM_ID)
        private platformId: any,
        private http: HttpClient,
        /* @SkipSelf() @Optional() private pixel: PixelService, */
        private $gaService: GoogleAnalyticsService,
    ) {
        if (isPlatformBrowser(this.platformId)) {
            this.load();
            this.calc();
        }
    }

    add(product: any, quantity: number, options: { name: string; value: string }[] = []): Observable<CartItem> {
        // timer only for demo
        return timer(1000).pipe(map(() => {
            this.onAddingSubject$.next(product);

            let item = this.items.find(eachItem => {

                //Check if item is there so ++quantity or insert new object (found in if else below)
                if (eachItem.product._id !== product._id || eachItem.options.length !== options.length) {
                    return false;
                }

                if (eachItem.options.length) {
                    for (const option of options) {
                        if (!eachItem.options.find(itemOption => itemOption.name === option.name && itemOption.value === option.value)) {
                            return false;
                        }
                    }
                }

                return true;
            });

            if (item) {
                item.quantity += quantity;
            } else {
                item = { product, quantity, options };

                this.data.items.push(item);
            }

            this.save();
            this.calc();

            return item;
        }));
    }

    update(updates: { item: CartItem, quantity: number }[]): Observable<void> {
        // timer only for demo
        return timer(1000).pipe(map(() => {
            updates.forEach(update => {
                const item = this.items.find(eachItem => eachItem === update.item);

                if (item) {
                    item.quantity = update.quantity;
                }
            });

            this.save();
            this.calc();
        }));
    }

    remove(item: CartItem): Observable<void> {
        // timer only for demo
        return timer(1000).pipe(map(() => {
            this.data.items = this.data.items.filter(eachItem => eachItem !== item);

            this.save();
            this.calc();
        }));
    }

    private calc(): void {
        let quantity = 0;
        let subtotal = 0;

        this.data.items.forEach(item => {
            quantity += item.quantity;
            subtotal += (item.product.taxedPrice || item.product.price) * item.quantity;
        });

        const totals: CartTotal[] = [];

        totals.push({
            title: 'Shipping',
            price: 25,
            type: 'shipping'
        });
        totals.push({
            title: 'Tax',
            price: subtotal * 0.20,
            type: 'tax'
        });

        const total = subtotal + totals.reduce((acc, eachTotal) => acc + eachTotal.price, 0);

        this.data.quantity = quantity;
        this.data.subtotal = subtotal;
        this.data.totals = totals;
        this.data.total = total;

        this.itemsSubject$.next(this.data.items);
        this.quantitySubject$.next(this.data.quantity);
        this.subtotalSubject$.next(this.data.subtotal);
        this.totalsSubject$.next(this.data.totals);
        this.totalSubject$.next(this.data.total);
    }

    private save(): void {
        //localStorage.setItem('cartItems', JSON.stringify(this.data.items));
    }

    private load(): void {
        // const items = localStorage.getItem('cartItems');

        // if (items) {
        //    this.data.items = JSON.parse(items);
        //}
    }

    clearCartItems(cartId) {
        let carts = this.cartItemsSubject$.value;

        let cartIndex = _.findIndex(carts, ["_id", cartId]);
        if (cartIndex > -1) {
            carts.splice(cartIndex, 1);
            this.cartItemsSubject$.next(carts);
            this.calculateCart();
        }
    }

    addItem(cartItem, product?) {

        if (product) {
            if (isPlatformBrowser(this.platformId)) {
                /* this.pixel?.track('AddToCart', {
                    content_ids: [product._id, product.code],  // Item SKUs
                    content_name: product.name.en,                         // Value of all items
                    content_type: product.type,
                    contents: [{ "id": product._id, "quantity": cartItem.amount || 1 }],
                    currency: "SAR",
                    value: product.taxedPrice          // Currency of the value
                }) */

                this.$gaService.gtag('event', 'add_to_cart', {
                    "items": [
                        {
                            "id": product.code,
                            "name": product.name?.en,
                            "brand": product.brandData?.name,
                            "category": product.companyData?.name?.en,
                            "quantity": product.quantity?.amount,
                            ...(product.taxedPrice) ? { "price": product.taxedPrice || product.price } : {}
                        }
                    ]
                })
            }
        }

        return this.http.post<any>(`${environment.apiUrl}/cart/add`, cartItem).pipe(
            tap(data => {
                if (data?.code == 200) {
                    this.setCart(data.cart)
                }
            }));
    }

    setCart(carts) {
        if (_.isArray(carts)) {
            this.cartItemsSubject$.next(carts)
        } else {
            this.cartItemsValue = carts;
        }
        this.calculateCart();
    }

    calculateCart() {
        let cartQuantity = 0;
        let cartSubtotal = 0;
        let cartTotal = 0;
        let shippingTotal = 0;
        let taxTotal = 0;
        let discountTotal = 0;
    // Use current carts; do not reset here
    _.forEach(this.cartItemsValue, (cartItem, key) => {
            cartSubtotal += cartItem.subtotal;
            cartTotal += cartItem.total;
            shippingTotal += cartItem.shippingTotal;
            taxTotal += cartItem.taxTotal;
            discountTotal += cartItem.discountTotal;
            _.forEach(cartItem.items, (item) => {
                item.product.discountPrice = this.calculateDiscount(item.product);
                cartQuantity += item.amount;
            });
        });

        this.subtotalSubject$.next(cartSubtotal);
        this.totalSubject$.next(cartTotal);
        this.shippingTotalSubject$.next(shippingTotal);
        this.taxTotalSubject$.next(taxTotal);
        this.discountTotalSubject$.next(discountTotal);
        this.quantitySubject$.next(cartQuantity);
    }

    getCart(userData?) {
        return this.http.post<any>(`${environment.apiUrl}/cart/get-carts`, userData).pipe(
            tap(data => {
                this.setCart(data.carts);
            })
        );
    }

    updateCartItem(params) {
        return this.http.post<any>(`${environment.apiUrl}/cart/set-amount`, params).pipe(
            tap(data => {
                this.setCart(data.cart);
            })
        );
    }

    saveCartId(params) {
        return this.http.post<any>(`${environment.apiUrl}/cart/save-cartId`, { params: params })
    }
    getSessionCartId() {
        return this.http.get<any>(`${environment.apiUrl}/cart/get-cartId`);
    }

    getCartDetails(cartId) {
        return this.http.post<any>(`${environment.apiUrl}/cart/get-cart`, { cartId: cartId })
    }

    checkout(params, userId?) {
        return this.http.post<any>(`${environment.apiUrl}/cart/checkout`, params).pipe(
            tap(data => {
                // let params: any = {}
                // if (userId) params.userId = userId;
               // this.clearCartItems(params.cartId);
            })
        )
    }

    setAddress(address) {
        return this.http.post<any>(`${environment.apiUrl}/cart/set-address`, address)
    }

    getShippings(cartId, shippingAddress) {
        return this.http.post<any>(`${environment.apiUrl}/cart/get-shippings`, { cartId: cartId,shippingAddress })
    }

    getPayments(cartId) {
        return this.http.post<any>(`${environment.apiUrl}/cart/get-payments`, { cartId: cartId })
    }

    setShipping(shipping) {
        return this.http.post<any>(`${environment.apiUrl}/cart/set-shipping`, shipping)
    }

    setPayment(payment) {
        return this.http.post<any>(`${environment.apiUrl}/cart/set-payment`, payment)
    }

    clearCart(userId?) {
        return this.http.post<any>(`${environment.apiUrl}/cart/clear-cart`, {}).pipe(
            tap(cart => {
                this.cartItemsSubject$.next([])
                this.calculateCart();
            })
        )
    }
    calculateDiscount(product) {
        let discountedPrice;
        if (product?.discountValue?.value && product?.discountValue.type != 'N') {
            discountedPrice = (product?.discountValue.type == 'F') ? product.taxedPrice || product.price - product.discountValue?.value : product.taxedPrice || product.price - product.discountValue?.value * product.taxedPrice || product.price / 100;
        }
        return discountedPrice;
    }

    applyCoupon(params) {
        return this.http.post<any>(`${environment.apiUrl}/cart/add-coupon`, params).pipe(
            tap((data) => {
                this.setCart(data.cart);
            })
        )
    }

    removeCoupon(params) {
        return this.http.post<any>(`${environment.apiUrl}/cart/remove-coupon`, params).pipe(
            tap((data) => {
                this.setCart(data.cart);
            })
        )
    }

    setNote(params) {
        return this.http.post<any>(`${environment.apiUrl}/cart/set-note`, params)
    }

    addLoyalty(params) {
        return this.http.post<any>(`${environment.apiUrl}/cart/add-loyalty`, params).pipe(
            tap((data) => {
                this.setCart(data.cart);
            })
        )
    }

    useCreditFacility(params) {
        return this.http.post<any>(`${environment.apiUrl}/cart/use-credit-facility`, params).pipe(
            tap((data) => {
                this.setCart(data.cart);
            })
        )
    }
    unuseCreditFacility(params) {
        return this.http.post<any>(`${environment.apiUrl}/cart/unuse-credit-facility`, params).pipe(
            tap((data) => {
                this.setCart(data.cart);
            })
        )
    }

    useVouchers(params) {
        return this.http.post<any>(`${environment.apiUrl}/cart/use-vouchers`, params).pipe(
            tap((data) => {
                this.setCart(data.cart);
            })
        )
    }
    unuseVouchers(params) {
        return this.http.post<any>(`${environment.apiUrl}/cart/unuse-vouchers`, params).pipe(
            tap((data) => {
                this.setCart(data.cart);
            })
        )
    }


    removeLoyalty(params) {
        return this.http.post<any>(`${environment.apiUrl}/cart/remove-loyalty`, params).pipe(
            tap((data) => {
                this.setCart(data.cart);
            })
        )
    }
    updateCartSelection(params) {
        return this.http.post<any>(`${environment.apiUrl}/cart/update-selection`, params).pipe(
            tap(data => {
                this.setCart(data.cart);
            })
        );
    }
}
